"use client";

import { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import { User, GraduationCap, Briefcase, MapPin } from "lucide-react";
import Header from "@/components/Header";
import JourneyCTA from "@/components/JourneyCTA";
import RegisterToViewPopup from "@/components/RegisterToViewPopup";
import { homeFaqItems } from "@/data/homeFaq";
import { trackEvent } from "@/components/GoogleAnalytics";

const IMAGE_CDN =
  process.env.NEXT_PUBLIC_IMAGE_CDN_URL || "";
const REGISTER_URL = "https://marathivadhuvarsuchak.com/register";

interface Location {
  country: string;
  state: string;
  city: string;
}

interface DisplayPicture {
  photoUrl: string;
  largeThumbnailUrl: string;
  smallThumbnailUrl: string;
}

interface PublicProfile {
  memberName: string;
  memberId: string;
  age: number;
  education: string;
  occupation: string;
  caste: string;
  location: Location;
  profileFor: string;
  displayPicture: DisplayPicture | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  hasNextPage: boolean;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    profiles: PublicProfile[];
    pagination: Pagination;
  };
}

function getProfilePhotoUrl(
  displayPicture: DisplayPicture | null
): string | null {
  if (!displayPicture?.smallThumbnailUrl) return null;
  const path = displayPicture.smallThumbnailUrl;
  const base =
    process.env.NEXT_PUBLIC_IMAGE_CDN_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    "";
  if (path.startsWith("http")) return path;
  return base + path;
}

const BRIDES_CACHE_KEY_PREFIX = "tawde_brides_public_female_";
const BRIDES_CACHE_TTL_MS = 4 * 60 * 60 * 1000;

function getBridesCacheKey(pageNum: number): string {
  return `${BRIDES_CACHE_KEY_PREFIX}${pageNum}`;
}

function getBridesCached(
  pageNum: number
): { profiles: PublicProfile[]; hasNextPage: boolean } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(getBridesCacheKey(pageNum));
    if (!raw) return null;
    const { profiles, hasNextPage, fetchedAt } = JSON.parse(raw) as {
      profiles: PublicProfile[];
      hasNextPage: boolean;
      fetchedAt: number;
    };
    if (Date.now() - fetchedAt > BRIDES_CACHE_TTL_MS) return null;
    return { profiles, hasNextPage };
  } catch {
    return null;
  }
}

function setBridesCache(
  pageNum: number,
  profiles: PublicProfile[],
  hasNextPage: boolean
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      getBridesCacheKey(pageNum),
      JSON.stringify({ profiles, hasNextPage, fetchedAt: Date.now() })
    );
  } catch {
    // ignore
  }
}

export default function BridesClient() {
  const [profiles, setProfiles] = useState<PublicProfile[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMorePopupOpen, setNoMorePopupOpen] = useState(false);
  const [viewProfilePopupOpen, setViewProfilePopupOpen] = useState(false);

  const fetchProfiles = useCallback(
    async (pageNum: number, append: boolean) => {
      const baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
      if (!baseUrl) return;
      const cached = getBridesCached(pageNum);
      if (cached) {
        if (append) {
          setProfiles((prev) => [...prev, ...cached.profiles]);
        } else {
          setProfiles(cached.profiles);
        }
        setHasNextPage(cached.hasNextPage);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await fetch(
          `${baseUrl}/profiles/public/female?page=${pageNum}`
        );
        const json: ApiResponse = await res.json();
        if (json.status === "success" && json.data) {
          const { profiles: nextProfiles, pagination } = json.data;
          const nextHasNext = pagination.hasNextPage ?? false;
          setBridesCache(pageNum, nextProfiles, nextHasNext);
          if (append) {
            setProfiles((prev) => [...prev, ...nextProfiles]);
          } else {
            setProfiles(nextProfiles);
          }
          setHasNextPage(nextHasNext);
        }
      } catch {
        setHasNextPage(false);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchProfiles(1, false);
  }, [fetchProfiles]);

  const handleShowMore = () => {
    if (!hasNextPage) {
      setNoMorePopupOpen(true);
      return;
    }
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProfiles(nextPage, true);
  };

  const handleProfileClick = () => {
    setViewProfilePopupOpen(true);
  };

  const bannerUrl = IMAGE_CDN
    ? `${IMAGE_CDN}/assets/img/marathi-vadhu-var-suchak-banner.jpg`
    : "/assets/img/marathi-vadhu-var-suchak-banner.jpg";

  return (
    <div className="bg-white">
      <Header variant="transparent" />

      <section className="relative flex min-h-[450px] h-[70vh] max-[599px]:h-[75vh] items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={bannerUrl}
            alt="Marathi Brides Banner"
            className="h-full w-full object-cover"
            style={{ objectFit: "cover" }}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="absolute inset-0 bg-black/40" />
        <div className="container relative z-10 mx-auto mb-[60px] px-4 max-[599px]:mb-[50px]">
          <div className="max-w-4xl">
            <h1
              className="hero-heading mb-4 text-[3.2rem] font-bold text-white"
              style={{
                textShadow: "1px 1px 3px rgba(0, 0, 0, 0.2)",
                lineHeight: "1.16",
              }}
            >
              मराठी वधू - वरांची पहिली पसंती
            </h1>
            <p
              className="mb-8 max-w-2xl text-xl text-white/90"
              style={{ textShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)" }}
            >
              सुशिक्षित मराठी वधूंची यादी पाहून फ्री नोंदणी करा
            </p>
            <Link
              href={REGISTER_URL}
              className="inline-block rounded-full bg-primary-600 px-8 py-4 text-lg font-medium tracking-wide text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-primary-700"
              onClick={() =>
                trackEvent("register_click", {
                  location: "brides_hero",
                })
              }
            >
              Register Free
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl text-center mb-10">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              तुमच्या आयुष्याचा जोडीदार फक्त एका क्लीकवर!
            </h2>
            <p className="mx-auto mb-8 max-w-3xl text-lg text-gray-600">
              Marathi Brides - काही नोंदणीकृत प्रोफाईल्सची यादी. अधिक
              प्रोफाईल्ससाठी फ्री नोंदणी करा.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span
                className="cursor-default rounded-full bg-primary-600 px-5 py-2 text-base font-normal text-white shadow-lg"
                aria-current="page"
              >
                Brides ( वधू )
              </span>
              <Link
                href="/grooms"
                className="rounded-full border-2 border-gray-300 px-5 py-2 text-base font-normal text-gray-700 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                Grooms ( वर )
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 min-[420px]:grid-cols-2 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg"
                >
                  <div className="relative aspect-[1/1] max-[599px]:aspect-[16/15] overflow-hidden bg-gray-100">
                    <div className="absolute inset-0 bg-gray-200 animate-shimmer-skeleton" />
                    <div
                      className="pointer-events-none absolute bottom-0 left-0 right-0 pl-6 pr-4 py-4 max-[599px]:pl-4 max-[599px]:pr-3"
                      aria-hidden
                    >
                      <div className="mb-2 h-5 w-2/3 rounded bg-gray-300/80" />
                      <div className="h-4 w-1/2 rounded bg-gray-300/60" />
                    </div>
                  </div>
                  <div className="px-5 py-5 max-[599px]:px-4">
                    <div className="mb-4 space-y-2.5">
                      <div className="relative h-3.5 max-w-[80%] overflow-hidden rounded bg-gray-200 animate-shimmer-skeleton" />
                      <div className="relative h-3.5 max-w-[70%] overflow-hidden rounded bg-gray-200 animate-shimmer-skeleton" />
                      <div className="relative h-3.5 max-w-[75%] overflow-hidden rounded bg-gray-200 animate-shimmer-skeleton" />
                      <div className="relative h-3.5 max-w-[90%] overflow-hidden rounded bg-gray-200 animate-shimmer-skeleton" />
                    </div>
                    <div className="relative h-4 w-24 overflow-hidden rounded bg-gray-200 animate-shimmer-skeleton" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 min-[420px]:grid-cols-2 lg:grid-cols-4">
                {profiles.map((profile) => {
                  const photoUrl = getProfilePhotoUrl(profile.displayPicture);
                  const locationStr = [
                    profile.location?.city,
                    profile.location?.state,
                    profile.location?.country,
                  ]
                    .filter(Boolean)
                    .join(", ");
                  const cityForAlt =
                    [
                      profile.location?.city,
                      profile.location?.state,
                      profile.location?.country,
                    ]
                      .filter(Boolean)
                      .find(
                        (s) =>
                          s &&
                          String(s).trim() !== "" &&
                          String(s) !== "Not specified"
                      ) || "India";
                  const casteForAlt = profile.caste?.trim() || "Marathi";
                  const imageAlt = `${casteForAlt} Vadhu ${cityForAlt} - ${casteForAlt} Bride ${profile.memberName} ${profile.age} Years`;
                  return (
                    <div
                      key={profile.memberId}
                      role="button"
                      tabIndex={0}
                      onClick={handleProfileClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleProfileClick();
                        }
                      }}
                      className="cursor-pointer overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg transition-shadow hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      <div className="relative aspect-[1/1] max-[599px]:aspect-[16/15] bg-gray-100">
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={imageAlt}
                            className="h-full w-full object-cover"
                            loading="lazy"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center bg-primary-50">
                            <span className="text-4xl font-semibold text-primary-600">
                              {profile.memberName.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div
                          className="absolute bottom-0 left-0 right-0 pl-6 pr-4 py-4 max-[599px]:pl-4 max-[599px]:pr-3"
                          style={{
                            background:
                              "linear-gradient(180deg, rgba(107, 57, 255, 0) 0%, rgba(0, 0, 0, 0.55) 54.17%, rgb(0, 0, 0) 100%)",
                          }}
                        >
                          <h3 className="truncate text-lg font-semibold text-white">
                            {profile.memberName}
                          </h3>
                          <p className="truncate text-sm text-white/80">
                            {profile.memberId}
                          </p>
                        </div>
                      </div>
                      <div className="px-5 py-5 max-[599px]:px-4">
                        <div className="mb-4 space-y-2.5 text-sm text-gray-600">
                          <div className="flex min-w-0 items-center gap-2">
                            <User className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span className="truncate">{profile.age} yrs</span>
                          </div>
                          <div className="flex min-w-0 items-center gap-2">
                            <GraduationCap className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span
                              className="truncate"
                              title={profile.education}
                            >
                              {profile.education}
                            </span>
                          </div>
                          <div className="flex min-w-0 items-center gap-2">
                            <Briefcase className="h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span
                              className="truncate"
                              title={profile.occupation}
                            >
                              {profile.occupation}
                            </span>
                          </div>
                          {locationStr && (
                            <div className="flex min-w-0 items-start gap-2">
                              <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                              <span className="break-words">{locationStr}</span>
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProfileClick();
                          }}
                          className="text-sm font-medium text-primary-600 hover:underline hover:text-primary-700"
                        >
                          View profile
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {profiles.length > 0 && (
                <div className="mt-10 flex justify-center">
                  <button
                    type="button"
                    onClick={handleShowMore}
                    disabled={loadingMore}
                    className="rounded-full bg-primary-600 px-8 py-4 text-lg font-medium text-white shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:bg-primary-700 disabled:opacity-60"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Loading...
                      </span>
                    ) : (
                      "Show more"
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-6xl">
            <div className="flex items-center max-[599px]:flex-col max-[599px]:gap-[50px]">
              <div className="w-1/2 max-[599px]:w-full flex items-center justify-center">
                {IMAGE_CDN && (
                  <Link href={REGISTER_URL} className="block">
                    <img
                      src={`${IMAGE_CDN}/assets/img/mvvs-male-free-premium-creative-3.jpg`}
                      alt="Male Members Free Registration Offer"
                      className="h-auto w-[420px] max-w-full cursor-pointer rounded-lg object-contain shadow-lg transition-opacity hover:opacity-90"
                      loading="lazy"
                    />
                  </Link>
                )}
              </div>
              <div className="w-1/2 pl-8 max-[599px]:w-full max-[599px]:pl-0 max-[599px]:text-center">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">
                  मराठी वरांना फ्री नोंदणी सोबत मिळणारे फायदे
                </h2>
                <ul className="space-y-4 text-lg text-gray-700">
                  {[
                    "फ्री ऑनलाईन सर्च करता येईल.",
                    "फ्री ऑनलाईन प्रोफाईल्स पाहता येतील.",
                    "फ्री ऑनलाईन प्रोफाईल लाईक करता येईल.",
                    "स्थळांना फ्री शॉर्टलिस्ट करता येणार.",
                    "स्थळांना फ्री इंटरेस्ट पाठवता येईल.",
                    "तसेच इतरही अनेक फ्री फायदे मिळतील.",
                  ].map((text, i) => (
                    <li key={i} className="flex items-start">
                      <svg
                        className="mr-3 mt-1 h-6 w-6 flex-shrink-0 text-primary-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link
                    href={REGISTER_URL}
                    className="inline-block rounded-full bg-primary-600 px-8 py-4 text-lg font-medium tracking-wide text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-primary-700"
                    onClick={() =>
                      trackEvent("register_click", {
                        location: "brides_benefits_section",
                      })
                    }
                  >
                    Register Free
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
            Success Stories
          </h2>
          <div className="mx-auto max-w-6xl">
            <div className="grid gap-8 md:grid-cols-3">
              <div className="rounded-2xl bg-white p-6 shadow-lg">
                <div className="mb-4 flex items-center">
                  <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                    <span className="font-semibold text-primary-600">M</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Mansi &amp; Mahesh Bait
                    </h3>
                    <p className="text-sm text-gray-600">Married in 2024</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;Thank you! I met Mansi through Marathi Vadhu Var Suchak and I'm happy to have found a perfect life partner.&rdquo;
                </p>
              </div>

              <div className="rounded-2xl bg-white p-6 shadow-lg">
                <div className="mb-4 flex items-center">
                  <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100">
                    <span className="font-semibold text-secondary-600">
                      K
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Kavya &amp; Siddhesh Ghag
                    </h3>
                    <p className="text-sm text-gray-600">Married in 2025</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;Met the love of my life in Marathi Vadhu Var Suchak. It all started when I got Kavya's profile as a recommendation on Marathi Vadhu Var Suchak.&rdquo;
                </p>
              </div>

              <div className="rounded-2xl bg-white p-6 shadow-lg">
                <div className="mb-4 flex items-center">
                  <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
                    <span className="font-semibold text-primary-600">A</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      Anita &amp; Nitin Surve
                    </h3>
                    <p className="text-sm text-gray-600">Married in 2025</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  &ldquo;Life is happier, thanks to Marathi Vadhu Var Suchak, I found Anita profile and families connected with each other.&rdquo;
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="bg-white py-16 sm:py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="mx-auto max-w-4xl space-y-4">
            {homeFaqItems.map((faq, index) => (
              <details
                key={index}
                className="group faq-details overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 text-left transition-colors hover:bg-gray-50 [&::-webkit-details-marker]:hidden">
                  <h3 className="pr-4 text-lg font-semibold text-gray-900">
                    {faq.question}
                  </h3>
                  <svg
                    className="h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-300 group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <div className="border-t border-gray-200 px-6 py-3 pb-4">
                  <p className="leading-relaxed text-gray-600">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <JourneyCTA
        title="Ready to Start Your Journey?"
        description="Discover meaningful connections through our trusted Marathi matrimony platform. Register free and browse thousands of verified Marathi brides."
        primaryButtonText="Register Free"
        primaryButtonLink={REGISTER_URL}
      />

      <RegisterToViewPopup
        isOpen={viewProfilePopupOpen}
        onClose={() => setViewProfilePopupOpen(false)}
        variant="fullProfile"
      />
      <RegisterToViewPopup
        isOpen={noMorePopupOpen}
        onClose={() => setNoMorePopupOpen(false)}
        variant="moreMatches"
      />
    </div>
  );
}
